import React, { useState, useEffect } from 'react';
import Sidebar from '../../../shared/components/Sidebar';
import PageLayout from '../../../shared/components/PageLayout';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Typography, Box, TextField, Button, Paper, Pagination,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar
} from '@mui/material';
import { collection, getDocs, query, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { useFirebase } from '../../../context/Firebase';

const Donation = () => {
    const { db } = useFirebase();
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [amount, setAmount] = useState('');
    const [donorName, setDonorName] = useState('');
    const [donorEmail, setDonorEmail] = useState('');
    const [donorPhone, setDonorPhone] = useState('');
    const [processing, setProcessing] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchDonations = async () => {
            try {
                setLoading(true);
                const q = query(collection(db, 'donations'), orderBy('date', 'desc'));
                const snapshot = await getDocs(q);
                const donationList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setDonations(donationList);
            } catch (error) {
                console.error('Error fetching donations:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDonations();
    }, [db]);

    const handleDonateClick = () => {
        if (!donorName.trim() || !amount || parseFloat(amount) <= 0) {
            setSnackbar({
                open: true,
                message: 'Please enter your name and a valid amount',
                severity: 'warning'
            });
            return;
        }
        setShowDialog(true);
    };

    const handleConfirmDonation = async () => {
        setProcessing(true);
        try {
            // Create donation record
            await addDoc(collection(db, 'donations'), {
                donorName: donorName.trim(),
                donorEmail: donorEmail.trim() || null,
                donorPhone: donorPhone.trim() || null,
                amount: parseFloat(amount),
                currency: 'INR',
                date: serverTimestamp(),
                status: 'Completed',
                transactionId: `TXN${Date.now()}`,
                paymentMethod: 'Manual Entry'
            });

            setSnackbar({
                open: true,
                message: 'Donation recorded successfully! Thank you for your contribution.',
                severity: 'success'
            });

            // Reset form
            setDonorName('');
            setDonorEmail('');
            setDonorPhone('');
            setAmount('');
            setShowDialog(false);

            // Refresh donations list
            const q = query(collection(db, 'donations'), orderBy('date', 'desc'));
            const snapshot = await getDocs(q);
            const donationList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDonations(donationList);
        } catch (error) {
            console.error('Error recording donation:', error);
            setSnackbar({
                open: true,
                message: 'Failed to record donation. Please try again.',
                severity: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    const paginatedDonations = donations.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (loading) {
        return (
            <div className="dashboard">
                <Sidebar />
                <div className="dashboard--content">
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                        <CircularProgress />
                    </Box>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <Sidebar />
            <div className="dashboard--content">
                <PageLayout title="Donation Management">
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Make a Donation</Typography>
                        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                            <TextField 
                                label="Your Name" 
                                value={donorName} 
                                onChange={(e) => setDonorName(e.target.value)} 
                                size="small"
                                required
                                sx={{ minWidth: 200 }}
                            />
                            <TextField 
                                label="Amount (₹)" 
                                type="number" 
                                value={amount} 
                                onChange={(e) => setAmount(e.target.value)} 
                                size="small"
                                required
                                inputProps={{ min: 1 }}
                                sx={{ minWidth: 150 }}
                            />
                            <Button 
                                variant="contained" 
                                onClick={handleDonateClick}
                                disabled={processing}
                                sx={{ height: 40 }}
                            >
                                Donate Now
                            </Button>
                        </Box>
                    </Paper>

                    {/* Donation Confirmation Dialog */}
                    <Dialog open={showDialog} onClose={() => !processing && setShowDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Confirm Donation</DialogTitle>
                        <DialogContent>
                            <Typography variant="body1" gutterBottom>
                                You are about to donate <strong>₹{amount}</strong>
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Email (Optional)"
                                    type="email"
                                    value={donorEmail}
                                    onChange={(e) => setDonorEmail(e.target.value)}
                                    size="small"
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Phone (Optional)"
                                    type="tel"
                                    value={donorPhone}
                                    onChange={(e) => setDonorPhone(e.target.value)}
                                    size="small"
                                />
                            </Box>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                This is a demo donation flow. In production, this would integrate with a payment gateway like Razorpay or Stripe.
                            </Alert>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowDialog(false)} disabled={processing}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleConfirmDonation} 
                                variant="contained" 
                                disabled={processing}
                            >
                                {processing ? <CircularProgress size={24} /> : 'Confirm Donation'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>

                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Donation History</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 600, color: '#555', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' } }}>
                                        <TableCell>Donor Name</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedDonations.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#999' }}>No donations found</TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedDonations.map((donation) => (
                                            <TableRow key={donation.id} hover>
                                                <TableCell>{donation.donorName || 'Anonymous'}</TableCell>
                                                <TableCell>₹{donation.amount || 0}</TableCell>
                                                <TableCell>{donation.date ? new Date(donation.date.seconds * 1000).toLocaleDateString() : 'N/A'}</TableCell>
                                                <TableCell>{donation.status || 'Completed'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {donations.length > itemsPerPage && (
                            <Box display="flex" justifyContent="center" mt={2}>
                                <Pagination count={Math.ceil(donations.length / itemsPerPage)} page={page} onChange={(e, value) => setPage(value)} color="primary" />
                            </Box>
                        )}
                    </Paper>
                </PageLayout>
            </div>
        </div>
    );
};

export default Donation;
